import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AttemptResult, QuestionPublic, api, CertSpec } from "../lib/api";
import QuestionCard from "../components/QuestionCard";

interface Result {
  domain: string;
  is_correct: boolean;
}

export default function MockExam() {
  const { certId } = useParams<{ certId: string }>();
  const [cert, setCert] = useState<CertSpec | null>(null);
  const [questions, setQuestions] = useState<QuestionPublic[]>([]);
  const [idx, setIdx] = useState(0);
  const [results, setResults] = useState<Result[]>([]);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!certId) return;
    api.getCert(certId).then((c) => {
      setCert(c);
      setSecondsLeft(c.duration_minutes * 60);
    });
  }, [certId]);

  useEffect(() => {
    if (!started || secondsLeft === null) return;
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => (s ?? 0) - 1), 1000);
    return () => clearTimeout(t);
  }, [started, secondsLeft]);

  const start = async () => {
    if (!cert) return;
    const qs = await api.practiceQuestions(cert.id, undefined, cert.total_questions);
    setQuestions(qs);
    setStarted(true);
  };

  const restart = () => {
    if (!cert) return;
    setStarted(false);
    setQuestions([]);
    setResults([]);
    setIdx(0);
    setSecondsLeft(cert.duration_minutes * 60);
  };

  const onAnswered = (r: AttemptResult) =>
    setResults((prev) => [...prev, { domain: r.domain, is_correct: r.is_correct }]);

  if (!cert) return <p>Chargement…</p>;

  if (!started) {
    return (
      <div className="card max-w-md mx-auto">
        <h1 className="font-display text-2xl mb-4">{cert.code} — Examen blanc</h1>
        <p className="text-sm mb-2">
          {cert.total_questions} questions · {cert.duration_minutes} min
        </p>
        <p className="text-xs text-scribe-600 mb-6">
          Conditions de l'examen réel : chrono, pas de feedback intermédiaire, score à la fin.
        </p>
        <button onClick={start} className="btn-primary w-full">
          Commencer
        </button>
      </div>
    );
  }

  if (started && questions.length === 0) {
    return (
      <div className="card max-w-md mx-auto text-center">
        <p className="text-sm text-scribe-600">Aucune question disponible pour cette certification.</p>
      </div>
    );
  }

  const timeUp = secondsLeft !== null && secondsLeft <= 0;
  const finished = idx >= questions.length || timeUp;

  if (finished) {
    const total = questions.length;
    const correct = results.filter((r) => r.is_correct).length;
    const answered = results.length;
    const scoreFrac = total > 0 ? correct / total : 0;
    const scorePct = Math.round(scoreFrac * 100);
    const passed = cert.passing_score !== null ? scoreFrac >= cert.passing_score : null;

    const byDomain: Record<string, { correct: number; total: number }> = {};
    for (const r of results) {
      if (!byDomain[r.domain]) byDomain[r.domain] = { correct: 0, total: 0 };
      byDomain[r.domain].total += 1;
      if (r.is_correct) byDomain[r.domain].correct += 1;
    }

    const verdict =
      passed === null
        ? { label: "Pas de seuil publié", cls: "bg-papyrus-200 text-ink-800" }
        : passed
          ? { label: "Réussi", cls: "bg-green-600/15 text-green-700" }
          : { label: "Échoué", cls: "bg-red-600/15 text-red-700" };

    return (
      <div className="card max-w-md mx-auto">
        <h2 className="font-display text-2xl mb-1">Examen terminé</h2>
        {timeUp && <p className="text-xs text-red-600 mb-3">Temps écoulé.</p>}

        <div className="flex items-end gap-3 mt-2 mb-1">
          <span className="font-display text-5xl">{scorePct}%</span>
          <span className={`text-xs font-mono uppercase tracking-wider px-2 py-1 rounded ${verdict.cls}`}>
            {verdict.label}
          </span>
        </div>
        <p className="text-sm text-scribe-600 mb-1">
          {correct} / {total} correctes
          {cert.passing_score !== null && ` · seuil ${Math.round(cert.passing_score * 100)}%`}
        </p>
        {answered < total && (
          <p className="text-xs text-scribe-600 mb-4">
            {total - answered} non répondue(s), comptée(s) comme fausse(s).
          </p>
        )}

        {Object.keys(byDomain).length > 0 && (
          <div className="mt-5">
            <p className="text-xs font-mono uppercase tracking-wider text-scribe-600 mb-2">
              Par domaine
            </p>
            <ul className="space-y-1 text-sm">
              {Object.entries(byDomain).map(([domain, s]) => (
                <li key={domain} className="flex justify-between gap-4">
                  <span className="min-w-0 truncate">{domain}</span>
                  <span className="font-mono text-scribe-600 shrink-0">
                    {s.correct}/{s.total}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mt-6">
          <button onClick={restart} className="btn-secondary w-full">
            Refaire
          </button>
          <Link to="/certs" className="btn-primary w-full">
            Retour
          </Link>
        </div>
      </div>
    );
  }

  const mins = Math.floor((secondsLeft ?? 0) / 60);
  const secs = (secondsLeft ?? 0) % 60;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-xs font-mono text-scribe-600">
          Question {idx + 1} / {questions.length}
        </p>
        <p className={`font-mono ${(secondsLeft ?? 0) < 300 ? "text-red-600" : "text-ink-700"}`}>
          {mins}:{secs.toString().padStart(2, "0")}
        </p>
      </div>
      <QuestionCard
        key={questions[idx].id}
        question={questions[idx]}
        mode="mock_exam"
        onAnswered={onAnswered}
        onNext={() => setIdx(idx + 1)}
      />
    </div>
  );
}
